import { AlertPriority, AlertSeverityFilterEnum, AlertStateFilterEnum } from "@/types/types";
import { getStartOfDayInLocalTimezone } from "@/utils/date-utils";

import { fhirR4 } from "@smile-cdr/fhirts";

/**
 * Maps a FHIR Task priority to an AlertSeverityFilterEnum value.
 */
export const mapPriorityToSeverity = (priority: string | undefined): AlertSeverityFilterEnum => {
  switch (priority) {
    case AlertPriority.stat:
      return AlertSeverityFilterEnum.critical;
    case AlertPriority.asap:
      return AlertSeverityFilterEnum.severe;
    case AlertPriority.urgent:
      return AlertSeverityFilterEnum.moderate;
    case AlertPriority.routine:
      return AlertSeverityFilterEnum.routine;
    default:
      return AlertSeverityFilterEnum.moderate;
  }
};

/**
 * Filters tasks by severity (based on priority mapping).
 */
export const filterTasksBySeverity = (tasks: fhirR4.Task[], severity: AlertSeverityFilterEnum): fhirR4.Task[] => {
  if (severity === AlertSeverityFilterEnum.all) {
    return tasks;
  }

  return tasks.filter(task => mapPriorityToSeverity(task.priority) === severity);
};

/**
 * Filters tasks by their active/resolved state.
 * Active = status is NOT completed.
 * Resolved = status IS completed AND lastModified is today (local timezone),
 *   unless includeAllResolved is true, in which case all completed tasks are returned.
 */
export const filterTasksByState = (
  tasks: fhirR4.Task[],
  state: AlertStateFilterEnum,
  options?: { includeAllResolved?: boolean }
): fhirR4.Task[] => {
  if (state === AlertStateFilterEnum.active) {
    return tasks.filter(task => task.status !== fhirR4.Task.StatusEnum.Completed);
  }

  if (options?.includeAllResolved) {
    return tasks.filter(task => task.status === fhirR4.Task.StatusEnum.Completed);
  }

  const startOfToday = getStartOfDayInLocalTimezone(new Date());
  return tasks.filter(
    task =>
      task.status === fhirR4.Task.StatusEnum.Completed &&
      task.lastModified &&
      new Date(task.lastModified) >= startOfToday
  );
};

/**
 * Returns counts of tasks per severity bucket.
 */
export const getAlertCountsBySeverity = (tasks: fhirR4.Task[]): Record<AlertSeverityFilterEnum, number> => {
  const counts: Record<AlertSeverityFilterEnum, number> = {
    [AlertSeverityFilterEnum.all]: 0,
    [AlertSeverityFilterEnum.severe]: 0,
    [AlertSeverityFilterEnum.critical]: 0,
    [AlertSeverityFilterEnum.moderate]: 0,
    [AlertSeverityFilterEnum.routine]: 0,
    [AlertSeverityFilterEnum.messages]: 0,
  };

  for (const task of tasks) {
    if (task.status !== fhirR4.Task.StatusEnum.Completed) {
      const severity = mapPriorityToSeverity(task.priority);
      counts[severity]++;
      counts[AlertSeverityFilterEnum.all]++;
    }
  }

  return counts;
};
